import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { FAQCategory, SuggestedQuestion } from '../types';
import { BaseUrl } from '../../utils/network';
import { getAuthToken } from '../../utils/functions';

export interface ChatbotApiResponse {
  categories?: FAQCategory[];
  suggested_questions?: SuggestedQuestion[];
}

export type GetFAQCategoriesResponse = FAQCategory[] | ChatbotApiResponse;

export const chatbotApi = createApi({
  reducerPath: 'chatbotApi',
  baseQuery: fetchBaseQuery({ 
    baseUrl: BaseUrl,
    prepareHeaders: (headers) => {
      const tokenConfig = getAuthToken();
      if (tokenConfig && tokenConfig.headers && tokenConfig.headers.Authorization) {
        headers.set('Authorization', tokenConfig.headers.Authorization);
      }
      return headers;
    }
  }),
  tagTypes: ['FAQ', 'ChatbotSettings'],
  endpoints: (builder) => ({
    getFAQCategories: builder.query<GetFAQCategoriesResponse, 'technology' | 'subscriber'>({
      query: (type) => `/masterdata/faq-categories/?chatbot_type=${type}`,
      providesTags: (result) => {
        const categories = Array.isArray(result) 
          ? result 
          : (result && typeof result === 'object' ? result.categories : []);
        return Array.isArray(categories)
          ? [...categories.map(({ id }) => ({ type: 'FAQ' as const, id: id?.toString() })), { type: 'FAQ', id: 'LIST' }]
          : [{ type: 'FAQ', id: 'LIST' }];
      },
    }),
    getFaqs: builder.query<any, { search?: string; legacy_source?: string; chatbot_type?: string; }>({
      query: (params) => ({
        url: '/masterdata/faqs/',
        params,
      }),
      providesTags: ['FAQ'],
    }),
    addFaq: builder.mutation<any, Partial<any>>({
      query: (body) => ({
        url: '/masterdata/faqs/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FAQ'],
    }),
    updateFaq: builder.mutation<any, { id: string | number; data: Partial<any> }>({
      query: ({ id, data }) => ({
        url: `/masterdata/faqs/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['FAQ'],
    }),
    deleteFaq: builder.mutation<any, string | number>({
      query: (id) => ({
        url: `/masterdata/faqs/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FAQ'],
    }),
    getChatbotSettings: builder.query<any, void>({
      query: () => '/masterdata/admin-chatbot-settings/',
      providesTags: ['ChatbotSettings'],
    }),
    updateChatbotSettings: builder.mutation<any, Partial<any>>({
      query: (data) => ({
        url: `/masterdata/admin-chatbot-settings/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['ChatbotSettings'],
    }),
  }),
});

export const { 
  useGetFAQCategoriesQuery,
  useGetFaqsQuery,
  useLazyGetFaqsQuery,
  useAddFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetChatbotSettingsQuery,
  useUpdateChatbotSettingsMutation
} = chatbotApi;

